import axios from 'axios';
import Button from 'react-bootstrap/Button';
import React, { useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import Card from 'react-bootstrap/Card';
import InputGroup from 'react-bootstrap/InputGroup';
import Badge from 'react-bootstrap/Badge';
import Spinner from 'react-bootstrap/Spinner';
import Modal from 'react-bootstrap/Modal';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingBox from '../../component/LoadingBox';
import MessageBox from '../../component/MessageBox';
import { Store } from '../../Store';
import { toast } from 'react-toastify';
import { getError } from '../../utils';
import { api } from '../../Api';

const PAGE_SIZE = 100;

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        points: action.payload.points,
        pages: action.payload.pages,
        countProducts: action.payload.countProducts,
        totalAmount: action.payload.totalAmount || 0,
        totalCharge: action.payload.totalCharge || 0,
        periodCounts: action.payload.periodCounts || { today: 0, week: 0, month: 0, total: 0 },
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

const POINT_TYPES = [
  { value: 'availablebalance', label: 'Available Balance' },
  { value: 'shoppingbalance', label: 'Shopping Balance' },
  { value: 'point', label: 'Register Balance' },
  { value: 'matchingpoint', label: 'Matching Point' },
  // Issued by an admin, not moved from the admin's own balance (server
  // refuses these for agents).
  { value: 'shoppingpoint', label: 'Shopping Point', adminOnly: true },
  { value: 'psipoint', label: 'Profit Sharing Incentive (PSI)', adminOnly: true },
];

// Matching points are a ledger of earned rows rather than a balance on the user
// document, so there is nothing to decrement — the backend rejects the type and
// points the admin at Matching History instead.
const DEDUCTIBLE_TYPES = POINT_TYPES.filter((t) => t.value !== 'matchingpoint');

const MODE_SEND = 'send';
const MODE_DEDUCT = 'deduct';

const USERNAME_IDLE = 'idle';
const USERNAME_CHECKING = 'checking';
const USERNAME_VALID = 'valid';
const USERNAME_INVALID = 'invalid';

const genTransferId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export default function PointsScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useContext(Store);
  const { userInfo } = state;

  const sp = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const page = parseInt(sp.get('page')) || 1;
  const qSearch = sp.get('search') || '';
  const qStart = sp.get('startDate') || '';
  const qEnd = sp.get('endDate') || '';

  const [{ loading, error, points, pages, countProducts, totalAmount, totalCharge, periodCounts }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
    points: [],
    pages: 1,
    countProducts: 0,
    totalAmount: 0,
    totalCharge: 0,
    periodCounts: { today: 0, week: 0, month: 0, total: 0 },
  });

  const fetchData = useCallback(async () => {
    try {
      dispatch({ type: 'FETCH_REQUEST' });
      const params = new URLSearchParams({ page, pageSize: PAGE_SIZE });
      if (qSearch) params.set('search', qSearch);
      if (qStart) params.set('startDate', qStart);
      if (qEnd) params.set('endDate', qEnd);
      const { data } = await axios.get(`${api}/api/points?${params.toString()}`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      dispatch({ type: 'FETCH_SUCCESS', payload: data });
    } catch (err) {
      dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
    }
  }, [page, qSearch, qStart, qEnd, userInfo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Send / deduct form state. Deduction is admin-only — taking a member's
  // balance away is a higher bar than handing one out, and the server enforces
  // the same rule with isAdmin.
  const canDeduct = Boolean(userInfo?.isAdmin);
  const [mode, setMode] = useState(MODE_SEND);
  const isDeduct = mode === MODE_DEDUCT;
  const [sendUsername, setSendUsername] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [pointType, setPointType] = useState('availablebalance');
  const [usernameStatus, setUsernameStatus] = useState(USERNAME_IDLE);
  const [receiverInfo, setReceiverInfo] = useState(null); // { name, username, phone }
  const [submitting, setSubmitting] = useState(false);

  // Sender identity (for self-transfer check only)
  const [myBalances, setMyBalances] = useState(null);
  const loadMyBalances = useCallback(async () => {
    try {
      const { data } = await axios.get(`${api}/api/users/mybalances`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      setMyBalances(data);
    } catch {
      setMyBalances(null);
    }
  }, [userInfo]);
  useEffect(() => {
    loadMyBalances();
  }, [loadMyBalances]);

  useEffect(() => {
    if (!sendUsername) {
      setUsernameStatus(USERNAME_IDLE);
      setReceiverInfo(null);
      return;
    }
    setUsernameStatus(USERNAME_CHECKING);
    const t = setTimeout(async () => {
      try {
        const resp = await axios.post(
          `${api}/api/users/checkusernameforpoint`,
          { value: sendUsername },
          { headers: { Authorization: `Bearer ${userInfo.token}` } }
        );
        if (resp.status === 200 && resp.data && resp.data.username) {
          setUsernameStatus(USERNAME_VALID);
          setReceiverInfo({
            name: resp.data.name,
            username: resp.data.username,
            phone: resp.data.phone || null,
          });
        } else {
          setUsernameStatus(USERNAME_INVALID);
          setReceiverInfo(null);
        }
      } catch {
        setUsernameStatus(USERNAME_INVALID);
        setReceiverInfo(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [sendUsername, userInfo]);

  // Confirmation modal state
  const [showConfirm, setShowConfirm] = useState(false);

  const numericAmount = Number(amount);
  const typeOptions = (isDeduct ? DEDUCTIBLE_TYPES : POINT_TYPES).filter(
    (t) => !t.adminOnly || userInfo.isAdmin
  );
  const selectedType = typeOptions.find((p) => p.value === pointType);
  const isSelfTransfer =
    receiverInfo && myBalances && receiverInfo.username === myBalances.username;

  // Switching to Deduct while Matching Point is selected would leave a type the
  // deduct endpoint refuses; fall back to the first deductible one.
  useEffect(() => {
    if (isDeduct && !DEDUCTIBLE_TYPES.some((t) => t.value === pointType)) {
      setPointType(DEDUCTIBLE_TYPES[0].value);
    }
  }, [isDeduct, pointType]);

  const preflightOk =
    usernameStatus === USERNAME_VALID &&
    !isSelfTransfer &&
    Number.isInteger(numericAmount) &&
    numericAmount > 0;

  const openConfirm = (e) => {
    if (e) e.preventDefault();
    if (usernameStatus !== USERNAME_VALID) {
      toast.error(
        isDeduct
          ? 'Please enter a valid member username.'
          : 'Please enter a valid receiver username.'
      );
      return;
    }
    if (isSelfTransfer) {
      toast.error(
        isDeduct
          ? 'You cannot deduct points from your own account.'
          : 'You cannot send points to your own account.'
      );
      return;
    }
    if (!Number.isInteger(numericAmount) || numericAmount <= 0) {
      toast.error('Enter a whole positive amount (no decimals, no zero).');
      return;
    }
    setShowConfirm(true);
  };

  const confirmCanSubmit = preflightOk;

  const submitHandler = async () => {
    if (!confirmCanSubmit) return;
    try {
      setSubmitting(true);
      const transferId = genTransferId();
      const endpoint = isDeduct ? 'deductpoint' : 'sendpoint';
      const resp = await axios.post(
        `${api}/api/users/${endpoint}`,
        {
          username: receiverInfo.username,
          pointType,
          PointAmount: numericAmount,
          transferId,
          ...(isDeduct ? { reason: reason.trim() } : {}),
        },
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );
      if (resp.status === 200) {
        const d = resp.data || {};
        if (isDeduct) {
          toast.success(
            d.targetName
              ? `Deducted ${numericAmount.toLocaleString()} from ${d.targetName}.`
              : 'Point deducted successfully.'
          );
        } else {
          toast.success(
            d.receiverName
              ? `Sent ${numericAmount.toLocaleString()} to ${d.receiverName}.`
              : 'Point sent successfully.'
          );
        }
        setSendUsername('');
        setAmount('');
        setReason('');
        setReceiverInfo(null);
        setUsernameStatus(USERNAME_IDLE);
        setShowConfirm(false);
        loadMyBalances();
        fetchData();
      } else {
        toast.error('Something went wrong.');
      }
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onAmountChange = (e) => {
    // strip anything non-digit so decimals, e, +, -, spaces, commas are rejected
    const cleaned = e.target.value.replace(/[^0-9]/g, '');
    setAmount(cleaned);
  };

  // Filter form state (local until user clicks Search)
  const [fSearch, setFSearch] = useState(qSearch);
  const [fStart, setFStart] = useState(qStart);
  const [fEnd, setFEnd] = useState(qEnd);

  useEffect(() => {
    setFSearch(qSearch);
    setFStart(qStart);
    setFEnd(qEnd);
  }, [qSearch, qStart, qEnd]);

  const buildPath = (overrides) => {
    const next = new URLSearchParams();
    const merged = { page: 1, search: qSearch, startDate: qStart, endDate: qEnd, ...overrides };
    if (merged.page && merged.page !== 1) next.set('page', merged.page);
    if (merged.search) next.set('search', merged.search);
    if (merged.startDate) next.set('startDate', merged.startDate);
    if (merged.endDate) next.set('endDate', merged.endDate);
    const q = next.toString();
    return `${location.pathname}${q ? `?${q}` : ''}`;
  };

  const applyFilters = (e) => {
    if (e) e.preventDefault();
    if (fStart && fEnd && fStart > fEnd) {
      toast.error('Start date must be before end date.');
      return;
    }
    navigate(buildPath({ page: 1, search: fSearch.trim(), startDate: fStart, endDate: fEnd }));
  };

  const resetFilters = () => {
    setFSearch('');
    setFStart('');
    setFEnd('');
    navigate(location.pathname);
  };

  const goToPage = (p) => {
    if (p < 1 || p > pages || p === page) return;
    navigate(buildPath({ page: p }));
  };

  const hasActiveFilter = Boolean(qSearch || qStart || qEnd);

  const pageNumbers = useMemo(() => {
    const nums = [];
    const windowSize = 2;
    const start = Math.max(1, page - windowSize);
    const end = Math.min(pages, page + windowSize);
    for (let i = start; i <= end; i++) nums.push(i);
    return nums;
  }, [page, pages]);

  const formatDateTime = (s) => {
    if (!s) return { date: '—', time: '', rel: '' };
    const d = new Date(s);
    if (isNaN(d)) return { date: String(s).substring(0, 10), time: '', rel: '' };
    const date = d.toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
    });
    const time = d.toLocaleTimeString(undefined, {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    });
    return { date, time, rel: relativeTime(d), iso: d.toISOString() };
  };

  const relativeTime = (d) => {
    const diffMs = Date.now() - d.getTime();
    const abs = Math.abs(diffMs);
    const sec = Math.round(abs / 1000);
    const min = Math.round(sec / 60);
    const hr = Math.round(min / 60);
    const day = Math.round(hr / 24);
    const future = diffMs < 0;
    const suffix = future ? 'from now' : 'ago';
    if (sec < 45) return `just now`;
    if (min < 60) return `${min} min ${suffix}`;
    if (hr < 24) return `${hr} hr ${suffix}`;
    if (day < 30) return `${day} day${day === 1 ? '' : 's'} ${suffix}`;
    const mo = Math.round(day / 30);
    if (mo < 12) return `${mo} mo ${suffix}`;
    const yr = Math.round(day / 365);
    return `${yr} yr ${suffix}`;
  };

  const formatAmount = (v) => {
    const n = Number(v);
    if (isNaN(n)) return v;
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const typeLabel = (info) => {
    if (!info) return null;
    // "Admin Send - Type: shoppingpoint" — read the type after "Type:" exactly,
    // since "shoppingpoint" also contains "point".
    const exact = (info.match(/type:\s*(\w+)/i) || [])[1];
    const m = POINT_TYPES.find((t) => t.value === (exact || info))
      || POINT_TYPES.find((t) => info.toLowerCase().includes(t.value));
    return m ? m.label : info;
  };

  const firstIndex = countProducts === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastIndex = Math.min(page * PAGE_SIZE, countProducts);

  return (
    <div>
      <Helmet>
        <title>Point Transfer</title>
      </Helmet>
      <Container fluid="lg" className="mb-4 pt-3">
        <div className="mb-3">
          <h4 className="mb-1 fw-semibold">Point Transfer</h4>
          <div className="text-muted small">
            Send points to users, deduct points back, and review the transfer log.
          </div>
        </div>

        {/* Send Point — compact single-row form */}
        <Card className="mb-3 border-0 shadow-sm">
          <Card.Body className="py-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <div className="small text-uppercase text-muted fw-semibold" style={{ letterSpacing: '0.05em' }}>
                {isDeduct ? 'Deduct Point' : 'Send Point'}
              </div>
              {canDeduct && (
                <div className="btn-group btn-group-sm" role="group" aria-label="Point action">
                  <Button
                    type="button"
                    variant={isDeduct ? 'outline-secondary' : 'dark'}
                    onClick={() => setMode(MODE_SEND)}
                  >
                    Send
                  </Button>
                  <Button
                    type="button"
                    variant={isDeduct ? 'danger' : 'outline-secondary'}
                    onClick={() => setMode(MODE_DEDUCT)}
                  >
                    Deduct
                  </Button>
                </div>
              )}
            </div>
            {isDeduct && (
              <div className="alert alert-warning py-2 small mb-3">
                The amount comes off the member's balance and goes back onto
                yours, so the move can be reversed with a Send. The member is
                notified, and the action is written to the staff activity log.
              </div>
            )}
            <Form onSubmit={openConfirm}>
              <Row className="g-2 align-items-end">
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label className="small text-muted mb-1">
                    {isDeduct ? 'Member' : 'Receiver'}
                  </Form.Label>
                  <InputGroup size="sm">
                    <Form.Control
                      placeholder="Username"
                      value={sendUsername}
                      onChange={(e) => setSendUsername(e.target.value.trim())}
                      autoComplete="off"
                      isInvalid={usernameStatus === USERNAME_INVALID}
                      isValid={usernameStatus === USERNAME_VALID && !isSelfTransfer}
                    />
                    {usernameStatus === USERNAME_CHECKING && (
                      <InputGroup.Text>
                        <Spinner animation="border" size="sm" />
                      </InputGroup.Text>
                    )}
                  </InputGroup>
                  <div className="small mt-1" style={{ minHeight: '1.1em' }}>
                    {usernameStatus === USERNAME_INVALID && (
                      <span className="text-danger">Username not found</span>
                    )}
                    {usernameStatus === USERNAME_VALID && receiverInfo && !isSelfTransfer && (
                      <span className="text-success">✓ {receiverInfo.name}</span>
                    )}
                    {isSelfTransfer && (
                      <span className="text-danger">
                        {isDeduct
                          ? 'Cannot deduct from yourself'
                          : 'Cannot send to yourself'}
                      </span>
                    )}
                  </div>
                </Col>
                <Col xs={12} sm={6} lg={3}>
                  <Form.Label className="small text-muted mb-1">Balance Type</Form.Label>
                  <Form.Select
                    size="sm"
                    value={pointType}
                    onChange={(e) => setPointType(e.target.value)}
                  >
                    {typeOptions.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </Form.Select>
                  <div style={{ minHeight: '1.1em' }} />
                </Col>
                <Col xs={12} sm={6} lg={3}>
                  <Form.Label className="small text-muted mb-1">Amount</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    inputMode="numeric"
                    placeholder="0"
                    value={amount}
                    onChange={onAmountChange}
                    autoComplete="off"
                  />
                  <div style={{ minHeight: '1.1em' }} />
                </Col>
                <Col xs={12} sm={6} lg={2}>
                  <Button
                    type="submit"
                    variant={isDeduct ? 'danger' : 'dark'}
                    size="sm"
                    className="w-100"
                    disabled={submitting || !preflightOk}
                  >
                    {isDeduct ? 'Deduct' : 'Send'}
                  </Button>
                  <div style={{ minHeight: '1.1em' }} />
                </Col>
                {isDeduct && (
                  <Col xs={12}>
                    <Form.Label className="small text-muted mb-1">
                      Reason (optional — shown to the member)
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      type="text"
                      maxLength={200}
                      placeholder="e.g. Reversal of a duplicate topup"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                  </Col>
                )}
              </Row>
            </Form>
          </Card.Body>
        </Card>

        {/* Confirmation Modal */}
        <Modal
          show={showConfirm}
          onHide={() => !submitting && setShowConfirm(false)}
          backdrop="static"
          keyboard={!submitting}
          centered
        >
          <Modal.Header closeButton={!submitting}>
            <Modal.Title>
              {isDeduct ? 'Confirm Deduction' : 'Confirm Transfer'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex justify-content-between py-2 border-bottom">
              <span className="text-muted">{isDeduct ? 'Member' : 'Receiver'}</span>
              <strong>{receiverInfo?.name} (@{receiverInfo?.username})</strong>
            </div>
            <div className="d-flex justify-content-between py-2 border-bottom">
              <span className="text-muted">Type</span>
              <strong>{selectedType?.label}</strong>
            </div>
            <div className="d-flex justify-content-between py-2">
              <span className="text-muted">Amount</span>
              <strong className={isDeduct ? 'text-danger' : 'text-success'}>
                {isDeduct ? '−' : '+'}
                {formatAmount(numericAmount)}
              </strong>
            </div>
            {isDeduct && (
              <>
                {reason.trim() && (
                  <div className="d-flex justify-content-between py-2 border-top">
                    <span className="text-muted">Reason</span>
                    <strong className="text-end">{reason.trim()}</strong>
                  </div>
                )}
                <div className="alert alert-danger py-2 small mb-0 mt-2">
                  This takes {formatAmount(numericAmount)} out of{' '}
                  {receiverInfo?.name}'s {selectedType?.label} and adds it to
                  yours. They will be notified.
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={() => setShowConfirm(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant={isDeduct ? 'danger' : 'success'}
              onClick={submitHandler}
              disabled={submitting || !confirmCanSubmit}
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" />{' '}
                  {isDeduct ? 'Deducting...' : 'Sending...'}
                </>
              ) : isDeduct ? (
                'Confirm & Deduct'
              ) : (
                'Confirm & Send'
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Summary — transfer counts by period */}
        {!loading && !error && (
          <Row className="g-2 mb-3">
            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-3">
                  <div className="small text-muted text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Today</div>
                  <div className="h4 mb-0 fw-semibold">{(periodCounts?.today || 0).toLocaleString()}</div>
                  <div className="small text-muted">transfers</div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-3">
                  <div className="small text-muted text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>This Week</div>
                  <div className="h4 mb-0 fw-semibold">{(periodCounts?.week || 0).toLocaleString()}</div>
                  <div className="small text-muted">transfers</div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-3">
                  <div className="small text-muted text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>This Month</div>
                  <div className="h4 mb-0 fw-semibold">{(periodCounts?.month || 0).toLocaleString()}</div>
                  <div className="small text-muted">transfers</div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-3">
                  <div className="small text-muted text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>All Time</div>
                  <div className="h4 mb-0 fw-semibold">{(periodCounts?.total || 0).toLocaleString()}</div>
                  <div className="small text-muted">transfers</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Results */}
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-white border-bottom py-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
              <div>
                <div className="fw-semibold">Transfer Log</div>
                <div className="small text-muted">
                  {loading ? 'Loading...' : countProducts > 0
                    ? `Showing ${firstIndex}–${lastIndex} of ${countProducts.toLocaleString()}`
                    : 'No records'}
                </div>
              </div>
              {!loading && !error && countProducts > 0 && (
                <div className="d-flex gap-3 small">
                  <div>
                    <span className="text-muted">Amount:</span>{' '}
                    <strong className="text-success">{formatAmount(totalAmount)}</strong>
                  </div>
                  <div>
                    <span className="text-muted">Charge:</span>{' '}
                    <strong className="text-warning">{formatAmount(totalCharge)}</strong>
                  </div>
                </div>
              )}
            </div>
            <Form onSubmit={applyFilters}>
              <Row className="g-2 align-items-end">
                <Col xs={12} md={4}>
                  <Form.Control
                    size="sm"
                    placeholder="Search by receiver username"
                    value={fSearch}
                    onChange={(e) => setFSearch(e.target.value)}
                  />
                </Col>
                <Col xs={6} md={3}>
                  <Form.Control
                    size="sm"
                    type="date"
                    placeholder="From"
                    value={fStart}
                    onChange={(e) => setFStart(e.target.value)}
                    max={fEnd || undefined}
                  />
                </Col>
                <Col xs={6} md={3}>
                  <Form.Control
                    size="sm"
                    type="date"
                    placeholder="To"
                    value={fEnd}
                    onChange={(e) => setFEnd(e.target.value)}
                    min={fStart || undefined}
                  />
                </Col>
                <Col xs={12} md={2} className="d-flex gap-2">
                  <Button type="submit" variant="dark" size="sm" className="flex-grow-1">
                    Search
                  </Button>
                  {hasActiveFilter && (
                    <Button type="button" variant="outline-secondary" size="sm" onClick={resetFilters}>
                      Reset
                    </Button>
                  )}
                </Col>
              </Row>
            </Form>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="p-4 text-center"><LoadingBox /></div>
            ) : error ? (
              <div className="p-3"><MessageBox variant="danger">{error}</MessageBox></div>
            ) : points.length === 0 ? (
              <div className="p-5 text-center text-muted">
                <div style={{ fontSize: '2rem' }}>📭</div>
                <div className="mt-2">No transfers found.</div>
                {hasActiveFilter && (
                  <Button variant="link" onClick={resetFilters}>Clear filters</Button>
                )}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped table-hover mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 50 }}>#</th>
                      <th>Date &amp; Time</th>
                      <th>Sender</th>
                      <th>Receiver</th>
                      <th className="text-end">Amount</th>
                      <th className="text-end">Charge</th>
                      <th>Type / Note</th>
                      <th>Status</th>
                      <th>ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {points.map((order, idx) => {
                      const dt = formatDateTime(order.createdAt);
                      const label = typeLabel(order.sendInfo);
                      return (
                        <tr key={order._id}>
                          <td className="text-muted">{firstIndex + idx}</td>
                          <td title={dt.iso}>
                            <div className="fw-semibold" style={{ whiteSpace: 'nowrap' }}>{dt.date}</div>
                            <div className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
                              {dt.time}
                            </div>
                            <div className="small text-primary" style={{ whiteSpace: 'nowrap' }}>
                              {dt.rel}
                            </div>
                          </td>
                          <td>{order.senderUsername || <span className="text-muted">—</span>}</td>
                          <td className="fw-semibold">{order.receiverUsername}</td>
                          {/* Admin deductions are logged as a negative amount,
                              so the sign in the log is the sign of the move. */}
                          <td
                            className={`text-end fw-bold ${
                              Number(order.amount) < 0 ? 'text-danger' : 'text-success'
                            }`}
                          >
                            {Number(order.amount) < 0 ? '−' : '+'}
                            {formatAmount(Math.abs(Number(order.amount)))}
                          </td>
                          <td className="text-end text-muted">
                            {order.transferCharge ? formatAmount(order.transferCharge) : '—'}
                          </td>
                          <td>
                            {String(order.sendInfo || '').includes('Admin Deduct') && (
                              <Badge bg="danger" className="me-1">Deducted</Badge>
                            )}
                            {label ? (
                              <Badge bg="light" text="dark" className="border">{label}</Badge>
                            ) : <span className="text-muted">—</span>}
                          </td>
                          <td>
                            {order.isPaid ? (
                              <Badge bg="success">Paid</Badge>
                            ) : (
                              <Badge bg="warning" text="dark">Pending</Badge>
                            )}
                          </td>
                          <td>
                            <code className="small text-muted" title={order._id}>
                              {order._id.substring(0, 8)}
                            </code>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card.Body>
          {!loading && !error && points.length > 0 && pages > 1 && (
            <Card.Footer className="bg-white">
              <nav aria-label="Point transfer pagination">
                <ul className="pagination pagination-sm justify-content-center mb-0 flex-wrap">
                  <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(1)} disabled={page === 1}>« First</button>
                  </li>
                  <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(page - 1)} disabled={page === 1}>‹ Prev</button>
                  </li>
                  {pageNumbers[0] > 1 && (
                    <li className="page-item disabled"><span className="page-link">…</span></li>
                  )}
                  {pageNumbers.map((p) => (
                    <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => goToPage(p)}>{p}</button>
                    </li>
                  ))}
                  {pageNumbers[pageNumbers.length - 1] < pages && (
                    <li className="page-item disabled"><span className="page-link">…</span></li>
                  )}
                  <li className={`page-item ${page === pages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(page + 1)} disabled={page === pages}>Next ›</button>
                  </li>
                  <li className={`page-item ${page === pages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(pages)} disabled={page === pages}>Last »</button>
                  </li>
                </ul>
                <div className="text-center small text-muted mt-2">
                  Page {page} of {pages}
                </div>
              </nav>
            </Card.Footer>
          )}
        </Card>
      </Container>
    </div>
  );
}
